import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ROLE_LABEL } from '@doit/shared';
import { LogOut, Menu, X } from 'lucide-react';
import { useAuth, useCurrentUser } from '../api/auth';
import { Logo } from '../components/ui';
import { navForRole } from '../lib/nav';

export function AppLayout() {
  const user = useCurrentUser();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const sections = navForRole(user.role);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex h-full">
      {/* ============ გვერდითი მენიუ ============ */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-60 shrink-0 flex-col border-r border-line bg-surface transition-transform lg:static lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-line px-4">
          <Logo />
          <button
            onClick={() => setMobileOpen(false)}
            className="rounded-md p-1.5 text-ink-mute hover:bg-canvas lg:hidden"
            aria-label="მენიუს დახურვა"
          >
            <X className="size-4" />
          </button>
        </div>

        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
          {sections.map((section) => (
            <div key={section.title}>
              <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-mute">
                {section.title}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        clsx(
                          'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition',
                          isActive
                            ? 'bg-brand-50 text-brand-700'
                            : 'text-ink-soft hover:bg-canvas hover:text-ink',
                        )
                      }
                    >
                      <item.icon className="size-4 shrink-0" />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.soon && (
                        <span className="rounded bg-canvas px-1.5 py-0.5 text-[9px] font-semibold uppercase text-ink-mute">
                          მალე
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-line p-3">
          <div className="flex items-center gap-2.5 rounded-lg px-2 py-2">
            <div className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-600 text-[12px] font-semibold text-white">
              {initialsOf(user.fullName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-ink">{user.fullName}</p>
              <p className="truncate text-[11px] text-ink-mute">{ROLE_LABEL[user.role]}</p>
            </div>
            <button
              onClick={() => void handleLogout()}
              title="გასვლა"
              aria-label="გასვლა"
              className="rounded-md p-1.5 text-ink-mute transition hover:bg-danger-soft hover:text-danger"
            >
              <LogOut className="size-4" />
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink/30 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      {/* ============ სამუშაო არე ============ */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center gap-3 border-b border-line bg-surface px-4 lg:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-md p-2 text-ink-soft hover:bg-canvas"
            aria-label="მენიუ"
          >
            <Menu className="size-5" />
          </button>
          <Logo showText={false} />
          <span className="text-[15px] font-semibold">IT Academy Step</span>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-6xl px-5 py-7">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('');
}
